import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardBody, Button, Table, Collapse } from 'reactstrap';
import api from '../../services/api';
import moment from 'moment';
import NewTimeslotModal from './NewTimeslotModal';
import EditTimeslotModal from './EditTimeslotModal';

const LeagueScheduleTimeslots = ({ schedule, serviceDuration }) => {
  const [timeslots, setTimeslots] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [newTimeslotModal, setNewTimeslotModal] = useState(false);
  const [editTimeslotModal, setEditTimeslotModal] = useState(false);
  const [editingTimeslot, setEditingTimeslot] = useState(null);

  const toggle = () => setIsOpen(!isOpen);
  const toggleNewModal = () => setNewTimeslotModal(!newTimeslotModal);
  const toggleEditModal = () => setEditTimeslotModal(!editTimeslotModal);

  const fetchTimeslots = useCallback(async () => {
    try {
      const response = await api.get(`/league-schedule-timeslots/?schedule=${schedule.id}`);
      setTimeslots(response.data);
    } catch (error) {
      console.error('Error fetching timeslots:', error);
    }
  }, [schedule.id]);

  useEffect(() => {
    if (schedule?.id) {
      fetchTimeslots();
    }
  }, [schedule?.id, fetchTimeslots]);

  const handleAddTimeslot = async (timeslotData) => {
    try {
      await api.post('/league-schedule-timeslots/', timeslotData);
      fetchTimeslots();
      toggleNewModal();
    } catch (error) {
      console.error('Error adding timeslot:', error);
      alert('Error adding timeslot: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditTimeslot = (timeslot) => {
    setEditingTimeslot(timeslot);
    toggleEditModal();
  };

  const handleSaveTimeslot = async (timeslotData) => {
    try {
      await api.put(`/league-schedule-timeslots/${editingTimeslot.id}/`, timeslotData);
      fetchTimeslots();
      toggleEditModal();
      setEditingTimeslot(null);
    } catch (error) {
      console.error('Error updating timeslot:', error);
      alert('Error updating timeslot: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteTimeslot = async (timeslotId) => {
    if (!window.confirm('Are you sure you want to delete this timeslot?')) {
      return;
    }
    try {
      await api.delete(`/league-schedule-timeslots/${timeslotId}/`);
      fetchTimeslots();
    } catch (error) {
      console.error('Error deleting timeslot:', error);
      alert('Error deleting timeslot: ' + (error.response?.data?.detail || error.message));
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    return moment(time, 'HH:mm:ss').format('h:mm A');
  };

  const numericServiceDuration = Number(serviceDuration) || 0;

  if (!schedule?.id) {
    return null;
  }

  return (
    <>
      <Card className="mt-2 border-secondary">
        <CardHeader
          onClick={toggle}
          className="cursor-pointer bg-light"
          style={{ cursor: 'pointer' }}
        >
          <small className="fw-semibold">
            {isOpen ? '▼' : '▶'} Timeslots ({timeslots.length})
          </small>
        </CardHeader>

        <Collapse isOpen={isOpen}>
          <CardBody className="p-2">
            {timeslots.length === 0 ? (
              <p className="text-muted small mb-2">No timeslots configured for this schedule.</p>
            ) : (
              <Table bordered hover size="sm" className="mb-2">
                <thead className="table-light">
                  <tr>
                    <th>Start</th>
                    <th>End</th>
                    <th>Priority</th>
                    <th style={{ width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timeslots.map((timeslot) => (
                    <tr key={timeslot.id}>
                      <td>{formatTime(timeslot.start)}</td>
                      <td>{formatTime(timeslot.end)}</td>
                      <td>{timeslot.priority ?? '-'}</td>
                      <td>
                        <Button color="primary" size="sm" onClick={() => handleEditTimeslot(timeslot)}>
                          Edit
                        </Button>{' '}
                        <Button color="danger" size="sm" onClick={() => handleDeleteTimeslot(timeslot.id)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <Button color="success" size="sm" onClick={toggleNewModal}>
              + Add Timeslot
            </Button>
          </CardBody>
        </Collapse>
      </Card>

      <NewTimeslotModal
        isOpen={newTimeslotModal}
        toggle={toggleNewModal}
        onSave={handleAddTimeslot}
        scheduleId={schedule?.id}
        serviceDuration={numericServiceDuration}
      />

      <EditTimeslotModal
        isOpen={editTimeslotModal}
        toggle={toggleEditModal}
        onSave={handleSaveTimeslot}
        timeslot={editingTimeslot}
        schedule={schedule}
        serviceDuration={numericServiceDuration}
      />
    </>
  );
};

export default LeagueScheduleTimeslots;
